import React, { useState, useEffect } from "react";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { getUserAuth } from "../../recoil/selectors";
import { patientProfileState } from "../../recoil/atoms";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  useModalAnalytics,
  ModalStep,
} from "../MissingBillingInformation/hooks/useModalAnalytics";
import { Banner } from "../Banner/Banner";
import { Button } from "../shared/Button/Button";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import InsuranceFileUploader, { FileUploadInfo } from "./InsuranceFileUploader";
import { InsuranceUploadType, RelationshipToPolicyOwner } from "./types";
import { submitInsuranceInfoForProcessing } from "./Services/InsuranceUploadService";
import { BillingSupportNumber } from "../UpfrontPayment/types";
import {
  ProfileModalWrapper,
  ProfileModalTitle,
  ProfileModalContentWrapper,
  ProfileModalTitleWrapper,
  ProfileModalButtons,
} from "../shared/Modal/Modal.styles";

interface InsuranceUploadFormProps {
  onComplete: () => void;
  onCancel: () => void;
  missingInsurance: boolean;
}

export const InsuranceUploadForm: React.FC<InsuranceUploadFormProps> = ({
  onComplete,
  onCancel,
  missingInsurance,
}) => {
  const { t } = useTranslation();
  const { deviceType } = useDeviceHook();
  const authInfo = useRecoilValue(getUserAuth);
  const [patientInfo, setPatientInfo] = useRecoilState(patientProfileState);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [relationshipToPolicyOwner, setRelationshipToPolicyOwner] =
    useState<RelationshipToPolicyOwner>(RelationshipToPolicyOwner.Self);
  const [insuranceUrls, setInsuranceUrls] = useState<FileUploadInfo>(
    patientInfo?.insuranceFileUrls || {},
  );
  const [readyToSubmit, setReadyToSubmit] = useState(false);

  // Analytics hook
  const { trackInteraction } = useModalAnalytics(
    true,
    ModalStep.Insurance,
    missingInsurance,
  );

  useEffect(() => {
    const hasFrontAndBack =
      insuranceUrls.front !== undefined && insuranceUrls.back !== undefined;
    setReadyToSubmit(hasFrontAndBack);
  }, [insuranceUrls]);

  const handleSubmit = async () => {
    if (!authInfo) {
      setError(t("insuranceUpload.errors.authenticationMissing"));
      return;
    }

    setLoading(true);
    trackInteraction("Insurance Upload", "Upload");

    try {
      const s3Urls = Object.values(insuranceUrls).map((file) => file.s3Url);
      const response = await submitInsuranceInfoForProcessing(
        s3Urls,
        relationshipToPolicyOwner,
        authInfo.access_token,
      );

      if (response.status === 422) {
        setError(
          t("insuranceUpload.errors.unableToProcess", {
            phone: BillingSupportNumber,
          }),
        );
      } else if (!response.ok) {
        setError(
          t("insuranceUpload.errors.submitFailed", {
            phone: BillingSupportNumber,
          }),
        );
      } else {
        const newProfile = await response.json();
        setPatientInfo(newProfile);
        setError(null);
        onComplete();
      }
    } catch (err) {
      setError(t("insuranceUpload.errors.unexpected"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProfileModalWrapper deviceType={deviceType}>
      <ProfileModalTitle deviceType={deviceType}>
        <ProfileModalTitleWrapper deviceType={deviceType}>
          {t("insuranceUpload.title")}
          <CloseIcon onClick={onCancel} />
        </ProfileModalTitleWrapper>
      </ProfileModalTitle>
      {loading ? (
        <ProfileModalContentWrapper>
          <SpinnerLoading />
        </ProfileModalContentWrapper>
      ) : (
        <>
          <ProfileModalContentWrapper>
            {error && (
              <Banner
                bannerType="warning"
                bannerTitle={t("insuranceUpload.errors.title")}
                bannerText={error}
              />
            )}

            <FormControl>
              <div>{t("insuranceUpload.fields.relationshipToPolicyOwner")}</div>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={relationshipToPolicyOwner}
                onChange={(event) =>
                  setRelationshipToPolicyOwner(
                    event.target.value as RelationshipToPolicyOwner,
                  )
                }
              >
                {Object.values(RelationshipToPolicyOwner).map((value) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={
                      <Radio sx={{ "&.Mui-checked": { color: "#017E72" } }} />
                    }
                    label={t(`insuranceUpload.relationships.${value}`)}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <InsuranceFileUploader
              setValue={setInsuranceUrls}
              insuranceType={InsuranceUploadType.primary}
            />
          </ProfileModalContentWrapper>
          <ProfileModalTitle deviceType={deviceType}>
            <ProfileModalButtons deviceType={deviceType}>
              <Button
                className="upfront-cancel-button"
                label={t("insuranceUpload.actions.cancel")}
                data-testid="back"
                onClick={onCancel}
                size="medium"
                reverse
                backgroundColor="greenDark"
                disabled={loading}
              />
              <Button
                className="upfront-submit-button"
                label={t("insuranceUpload.actions.upload")}
                data-testid="submit-button"
                disabled={!readyToSubmit || loading}
                onClick={handleSubmit}
                size="medium"
                backgroundColor="greenDark"
              />
            </ProfileModalButtons>
          </ProfileModalTitle>
        </>
      )}
    </ProfileModalWrapper>
  );
};
